import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  GitBranch, ArrowLeft, Sun, Moon, Monitor,
  Mail, KeyRound, Trash2, AlertTriangle, Loader2, CheckCircle,
} from 'lucide-react';

import { api } from '../utils/api';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, updateSettings, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [isImpersonating] = useState(() => !!localStorage.getItem('impersonating'));
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);

  // Change email state
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Reset password state
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  if (!user) {
    navigate('/');
    return null;
  }

  const hasPassword = user.has_password !== false;
  const googleLinked = !!user.google_linked;

  const blockReadOnly = () => {
    if (isImpersonating) {
      toast.error('Read-only while viewing as another user.', { id: 'readonly-impersonation' });
      return true;
    }
    return false;
  };

  const handleSave = async () => {
    if (blockReadOnly()) return;
    setSaving(true);
    try {
      await updateSettings({ name, theme_preference: theme });
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (blockReadOnly()) return;
    if (!newEmail.trim()) return;
    setEmailLoading(true);
    try {
      const { ok, data } = await api.post('/auth/change-email', { new_email: newEmail });
      if (ok) {
        setEmailSent(true);
        toast.success('Verification email sent');
      } else {
        toast.error(data.detail || 'Failed to send verification email');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (blockReadOnly()) return;
    setResetLoading(true);
    try {
      const { ok } = await api.post('/auth/forgot-password', { email: user.email });
      if (ok) {
        setResetSent(true);
        toast.success('Reset link sent to your email');
      } else {
        toast.error('Failed to send reset link');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const { ok, data } = await api.post('/auth/delete-account', { email: deleteConfirmEmail });
      if (ok) {
        setShowDeleteDialog(false);
        toast.success('Account scheduled for deletion');
        logout();
        navigate('/');
      } else {
        toast.error(data.detail || 'Failed to delete account');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      {/* Header */}
      <header className="h-14 border-b border-border flex items-center px-4">
        <div className="flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <GitBranch className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold">AutomataLab</span>
            <span className="text-muted-foreground">/</span>
            <span>Settings</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Manage your account settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isImpersonating}
                data-testid="settings-name"
              />
            </div>
          </CardContent>
        </Card>

        {/* Email */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <CardTitle>Email Address</CardTitle>
                <CardDescription>Change the email associated with your account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Current email</Label>
              <p className="text-sm font-medium" data-testid="settings-email">{user.email}</p>
            </div>
            {emailSent ? (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-950/30 rounded-lg p-3" data-testid="email-sent-msg">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>Verification email sent to <strong>{newEmail}</strong>. Check your inbox to confirm the change.</span>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Enter new email address"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  disabled={isImpersonating}
                  data-testid="new-email-input"
                />
                <Button
                  onClick={handleChangeEmail}
                  disabled={emailLoading || !newEmail.trim() || isImpersonating}
                  data-testid="change-email-btn"
                >
                  {emailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Password */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-muted-foreground" />
              <div>
                <CardTitle>{hasPassword ? 'Password' : 'Set a Password'}</CardTitle>
                <CardDescription>
                  {hasPassword
                    ? 'Reset your account password via email'
                    : 'Add password login to your Google-linked account'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {resetSent ? (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-950/30 rounded-lg p-3" data-testid="reset-sent-msg">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>{hasPassword ? 'Password reset' : 'Password setup'} link sent to <strong>{user.email}</strong>. Check your inbox.</span>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={handleResetPassword}
                disabled={resetLoading || isImpersonating}
                data-testid="reset-password-btn"
              >
                {resetLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                {hasPassword ? 'Send Reset Link' : 'Send Setup Link'}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Linked Accounts */}
        <Card>
          <CardHeader>
            <CardTitle>Linked Accounts</CardTitle>
            <CardDescription>Sign-in methods connected to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-sm font-medium">Google</span>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${googleLinked ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                  {googleLinked ? 'Connected' : 'Not connected'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <KeyRound className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Password</span>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${hasPassword ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                  {hasPassword ? 'Set' : 'Not set'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Customize the look and feel of the application</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Label>Theme</Label>
              <RadioGroup value={theme} onValueChange={setTheme} className="grid grid-cols-3 gap-4">
                <div>
                  <RadioGroupItem value="light" id="theme-light" className="peer sr-only" />
                  <Label
                    htmlFor="theme-light"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    data-testid="theme-light-option"
                  >
                    <Sun className="w-6 h-6 mb-2" />
                    <span className="text-sm font-medium">Light</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="dark" id="theme-dark" className="peer sr-only" />
                  <Label
                    htmlFor="theme-dark"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    data-testid="theme-dark-option"
                  >
                    <Moon className="w-6 h-6 mb-2" />
                    <span className="text-sm font-medium">Dark</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="system" id="theme-system" className="peer sr-only" />
                  <Label
                    htmlFor="theme-system"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                    data-testid="theme-system-option"
                  >
                    <Monitor className="w-6 h-6 mb-2" />
                    <span className="text-sm font-medium">System</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || isImpersonating} data-testid="save-settings-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Danger Zone */}
        <Card className="border-destructive/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              <div>
                <CardTitle className="text-destructive">Danger Zone</CardTitle>
                <CardDescription>Permanently delete your account and all data</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Once deleted, your account will be scheduled for permanent removal after 30 days.
              During this period, you can contact support to restore your account.
            </p>
            <Button
              variant="destructive"
              onClick={() => { if (blockReadOnly()) return; setShowDeleteDialog(true); }}
              disabled={isImpersonating}
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete Account
            </DialogTitle>
            <DialogDescription>
              This will delete your account and all your automata. Your data will be retained for 30 days before permanent deletion.
              Type your email <strong>{user.email}</strong> below to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type your email to confirm"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            data-testid="delete-confirm-email-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setShowDeleteDialog(false); setDeleteConfirmEmail(''); }} data-testid="delete-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirmEmail.toLowerCase() !== user.email.toLowerCase() || deleteLoading}
              onClick={handleDeleteAccount}
              data-testid="delete-confirm-btn"
            >
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete My Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
